<template>
    <transition name="fade">
        <div v-show="authUpdateOverlayStateStore.state.show"
             class="auth-update-overlay">
            <div class="container-sm h-100">
                <div class="row dummy-row"></div>
                <div class="row title-row">
                    <div class="col h2 title-col"
                         :style="{ fontSize: FontSizes.large2}">認証情報の更新</div>
                </div>
                <div class="row mb-2">
                    <div class="col text-left">サービスを利用するには<span :class="authStore.getters.shouldUpdatePassword ? 'text-should-update' : 'text-updated'">初期パスワードを変更</span>し、<span :class="authStore.getters.shouldVerifyMail ? 'text-should-update' : 'text-updated'">メールアドレスを登録</span>します。</div>
                </div>
                <div class="row">
                    <div class="col password-input-form input-form"
                         :class="{'input-form-should-update': authStore.getters.shouldUpdatePassword}">
                        <div class="container">
                            <div class="row">
                                <div class="col h3">パスワードの変更</div>
                            </div>
                            <div class="row justify-content-start">
                                <div class="col text-left">新しいパスワード</div>
                            </div>
                            <div class="row">
                                <PasswordInput v-model:password="password1"
                                               class="col"/>
                            </div>
                            <div class="row justify-content-start">
                                <div class="col text-left">新しいパスワード（確認）</div>
                            </div>
                            <div class="row">
                                <PasswordInput v-model:password="password2"
                                               class="col"/>
                            </div>
                            <div class="row justify-content-start my-2 warning-row"
                                 :style="{fontSize: FontSizes.small1}">
                                <div class="col text-left text-danger pw-warning-message">{{ pwWarningMessage }}</div>
                            </div>
                            <div class="row my-2 justify-content-center">
                                <button class="col-10 btn btn-primary"
                                        :disabled="!canClickUpdatePW"
                                        @click="onClickUpdatePassword">パスワードを変更する</button>
                            </div>
                        </div>
                        <transition name="fade">
                            <div v-show="showPWFormOverlay"
                                 class="password-input-form-overlay input-form-overlay">
                                <div class="updated-text">初期パスワード変更済み</div>
                                <button class="re-update-pw-button btn btn-info re-update-button"
                                        @click="onClickReChangePW">パスワードを再変更</button>
                            </div>
                        </transition>
                    </div>
                </div>
                <div class="row my-1">
                    <div class="col mail-input-form input-form"
                         :class="{'input-form-should-update': authStore.getters.shouldVerifyMail}">
                        <div class="container">
                            <div class="row">
                                <div class="col h3">メールアドレスの変更</div>
                            </div>
                            <div class="row justify-content-start">
                                <div class="col text-left">メールアドレス</div>
                            </div>
                            <div class="row">
                                <input type="email"
                                       v-model="email"
                                       class="col input-frame"/>
                            </div>
                            <div class="row justify-content-start">
                                <div class="col mail-explain  text-left">キャリアメールは非推奨とします(@docomo、@au、@softbankなど)。
                                    もしキャリアメールを使用される場合には、本システムからのメールを受信できない場合がありますが、これにより生じた損害等について当社が責任を負うものではないことを了承いただいたものとみなします。
                                </div>
                            </div>
                            <div class="row justify-content-start my-2">
                                <div class="col text-left">確認メールを送信し、届いたメールの「確認リンク」を押してください。<br>（確認メールの到達には数分かかることがあります。）</div>
                            </div>
                            <div class="row my-2 justify-content-center">
                                <button class="col-10 btn btn-primary"
                                        :disabled="!canClickSendMail()"
                                        @click="onClickSendVerificationMail">{{ sendMailButtonText() }}</button>
                            </div>
                        </div>
                        <transition name="fade">
                            <div v-show="showMailFormOverlay"
                                 class="mail-input-form-overlay input-form-overlay">
                                <div class="updated-text">メール確認済み</div>
                                <button class="re-update-mail-button btn btn-info re-update-button"
                                        @click="onClickReVerifyMail">メールアドレスを再変更</button>
                            </div>
                        </transition>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6 col-2"></div>
                    <button class="btn btn-secondary col-8 col-md-6"
                            @click="onClickCancelUpdate">{{ cancelText }}</button>
                </div>
                <div class="dummy-row"></div>
            </div>
        </div>
    </transition>
</template>

<script lang="ts">
import {computed, defineComponent, nextTick, ref, watch} from "vue";
import {authUpdateOverlayMode, authUpdateOverlayStateStore} from "@/stores/auth-update-overlay-state-store";
import { FontSizes } from "@/utils/font-sizes";
import PasswordInput from "@/components/PasswordInput.vue";
import {authStore} from "@/stores/auth-store";
import {logoutMessages, subPaths} from "@/utils/consts";
import {useRouter} from "vue-router";
import {backendApi} from "@/utils/backend-api";
import {toastStore} from "@/stores/toast-store";
import {certMailStatuses} from "@/data-models/auth";

export default defineComponent({
    name: "AuthUpdateOverlay",
    components: {
        PasswordInput,
    },
    setup() {

        const password1 = ref<string>(""),
                password2 = ref<string>(""),
                email = ref<string>(""),
                pwWarningMessage = ref<string>(""),
                canClickUpdatePW = ref<boolean>(false),
                mailSentOnce  = ref<boolean>(false),
                showPWFormOverlay = ref<boolean>(false),
                showMailFormOverlay = ref<boolean>(false),
                // 「パスワードを再変更」「メールアドレスを再変更」でユーザーが明示的にフォームを
                // 開き直した後、ポーリング（1秒毎）がまだ変わっていない現在のステータスを使って
                // showPWFormOverlay/showMailFormOverlayを毎tick無条件に上書きしてしまい、
                // ユーザーが何も入力・送信していないのに1秒ほどで再び「済み」表示に戻ってしまう
                // 問題があった。実際に送信するまでは上書きを止めるためのフラグ。
                isReChangingPassword = ref<boolean>(false),
                isReChangingMail = ref<boolean>(false)

        const cancelText = computed<string>((): string => {
            // このページで認証情報が更新され、確認が通ると文言が変わる。
            return authUpdateOverlayStateStore.state.mode === authUpdateOverlayMode.SHOULD_UPDATE
                && authStore.getters.shouldUpdateAuths
                    ? "認証情報を更新せずにログアウトする"
                    : "閉じる"
        })

        watch(() => password1.value, () => {
            validatePW()
        })

        watch(() => password2.value, () => {
            validatePW()
        })

        const validatePW = () => {

            canClickUpdatePW.value = false
            pwWarningMessage.value = ""

            // 全角禁止
            // 英数を1文字以上ずつ含む

            if (password1.value.length > 0) {

                // if (!new RegExp(/^[0-9a-zA-Z]*$/).test(password1.value)) {
                //     pwWarningMessage.value = "パスワードに記号や全角文字は使えません。（半角英数字のみ）"
                // } else if (!new RegExp(/(?=.*[a-zA-Z])(?=.*[0-9])/).test(password1.value)) {
                //     pwWarningMessage.value = "パスワードは英字・数字を1文字以上ずつ含む必要があります。"
                // } else if (password1.value.length < 8 || password1.value.length > 30) {
                //     pwWarningMessage.value = "パスワードの長さは8文字以上、30文字以内としてください。"
                // } else if (password2.value.length === 0) {
                //     pwWarningMessage.value = "確認用パスワードを入力してください。"
                // } else {
                if (password1.value.length < 12 || password1.value.length > 30) {
                    pwWarningMessage.value = "パスワードは12文字以上でご設定をお願いいたします。"
                } else if (password2.value.length === 0) {
                    pwWarningMessage.value = "確認用パスワードを入力してください。"
                } else if (password1.value !== password2.value) {
                    pwWarningMessage.value = "確認用パスワードが異なります。"
                } else {
                    if (password1.value === password2.value) {
                        canClickUpdatePW.value = true
                    } else {
                        pwWarningMessage.value = "確認用パスワードが異なります。"
                    }
                }
            } else {
                pwWarningMessage.value = "パスワードが入力されていません。"
            }
        }

        const resetInputs = () => {
            password1.value = ""
            password2.value = ""

            canClickUpdatePW.value = false

            email.value = ""
            mailSentOnce.value = false

            isReChangingPassword.value = false
            isReChangingMail.value = false

            nextTick(() => {
                pwWarningMessage.value = ""
            })
        }

        const hideOverlay = () => {
            authUpdateOverlayStateStore.commit("hideOverlay")
        }

        const router = useRouter()

        const onClickCancelUpdate = () => {

            // パスワード変更必須モードでキャンセルするとログアウトする。
            // またこの画面内で更新し、サーバに確認するとステータスが変化するので、ボタンの文言と挙動を変化させる。
            if (authUpdateOverlayStateStore.state.mode === authUpdateOverlayMode.SHOULD_UPDATE
                    && authStore.getters.shouldUpdateAuths) {
                authStore.commit("clearAuth")
                authStore.commit("clearToken")
                router.push({
                    path: subPaths.LOGGED_OUT,
                    query: {
                        logout_message: logoutMessages.NORMAL_LOGOUT.key
                    }
                })
            }

            hideOverlay()
        }

        const onClickUpdatePassword = async () => {

            const pw = password1.value

            pwWarningMessage.value = ""

            const resp = await backendApi.inquireUpdatePassword(authStore.state.auth.userId, pw)

            if (resp.status === 401) {
                // 本当にセッションが切れている場合のみログアウト処理をする。
                // （バリデーション失敗は401以外のステータスで返ってくるため、ここでは分けて判定する）
                password1.value = ""
                password2.value = ""
                authStore.commit("clearAuth")
                authStore.commit("clearToken")
                // このオーバーレイは常時マウントされておりCERT確認のポーリングが走っているため、
                // 明示的に閉じてポーリングを止める（閉じないと空トークンで/verify_certificateを
                // 呼び続け、401が延々返り続けることになる）。
                authUpdateOverlayStateStore.commit("hideOverlay")
                await router.push({
                    path: subPaths.LOGGED_OUT,
                    query: {
                        logout_message: logoutMessages.NORMAL_LOGOUT.key
                    }
                })
                return
            }

            if (resp.isSuccessful) {
                // 成功した時だけ入力欄をクリアする。
                password1.value = ""
                password2.value = ""

                // 実際に送信できたので、以降はポーリングによる状態反映を再開してよい。
                isReChangingPassword.value = false

                toastStore.commit("post", {
                    message: "パスワードを更新しました"
                })

                if (authUpdateOverlayStateStore.state.mode === authUpdateOverlayMode.NORMAL_UPDATE) {
                    // 自発更新モードの時はCERT検証を走らせていないので、変更が成功した時点で目隠しを再表示する。
                    showPWFormOverlay.value = true
                }

            } else {
                // バックエンドが具体的な失敗理由（例：漏洩パスワード検知）を description で返すので、
                // それをそのまま「新しいパスワード（確認）」欄の下に表示する。
                // 空/未設定のときのみ汎用フォールバックを使う。
                // 失敗時は入力し直す手間を減らすため、入力済みのパスワードは消さずに残す。
                pwWarningMessage.value = resp.description || "パスワード更新に失敗！"
            }
        }

        const canClickSendMail = (): boolean => {
            return email.value.length > 0
        }

        const onClickSendVerificationMail = async () => {

            // メアドをポストした時点でサーバ側がnot_verifiedに切り替わるので、アプリ側もnot_verifiedに切り替え問合せを再開する。
            authStore.commit("updateEmailValidity", {
                mail: certMailStatuses.notVerified
            })

            mailSentOnce.value = true
            const resp = await backendApi.sendVerificationMail(authStore.state.auth.userId, email.value)
            if (resp.isAuthorized) {
                if (resp.isSuccessful) {
                    // 実際に送信できたので、以降はポーリングによる状態反映（リンククリック後の検知）を
                    // 再開してよい。
                    isReChangingMail.value = false

                    toastStore.commit("post", {
                        message: "確認メールを送信しました。"
                    })

                    if (authUpdateOverlayStateStore.state.mode === authUpdateOverlayMode.NORMAL_UPDATE) {
                        // 自発更新モードの時はページを表示してすぐに「更新の必要なし」と判断し、確認問合せをやめてしまう。
                        // メアドをポストした時点でサーバ側がnot_verifiedに切り替わるので、アプリ側もnot_verifiedに切り替え問合せを再開する。
                        // 実際に問合せを開始するのはメール送信に成功してから
                        startCheckingCertStatus()
                    }

                } else {
                    toastStore.commit("post", {
                        message: "メール送信失敗！"
                    })
                }
            } else {
                authStore.commit("clearAuth")
                authStore.commit("clearToken")
                await router.push({
                    path: subPaths.LOGGED_OUT,
                    query: {
                        logout_message: logoutMessages.NORMAL_LOGOUT.key
                    }
                })
            }
        }

        let certCheckTimerId = 0

        const startCheckingCertStatus = () => {
            certCheckTimerId = window.setInterval(async () => {
                if (authStore.getters.shouldUpdateAuths) {
                    const resp = await backendApi.inquireCertStatus()
                    if (resp.isAuthorized) {
                        // /verify_certificate は { password, mail, mail_address } をフラットに返す
                        // （/login や /admin_login のように cert: {...} の下にネストされていない）。
                        // 以前は Auth.fromJson(resp.value) 経由で auth.cert.password/mail を読んでいたが、
                        // このレスポンスには cert プロパティ自体が存在しないため常に "" と誤認識され、
                        // 既に正しく設定済みのステータスをポーリングのたびに上書きして壊してしまっていた
                        // （＝オーバーレイが完了を検知できず、永久に自動で閉じられない主因）。
                        const certStatus = resp.value as { password: string, mail: string }
                        console.log(certStatus)

                        if (authUpdateOverlayStateStore.state.mode === authUpdateOverlayMode.SHOULD_UPDATE) {
                            // 要更新状態でこのページを開いた、

                            if (certStatus.password !== authStore.state.auth.cert.password) {
                                authStore.commit("updatePasswordValidity", {
                                    password: certStatus.password
                                })
                            }

                            if (certStatus.mail !== authStore.state.auth.cert.mail) {
                                authStore.commit("updateEmailValidity", {
                                    mail: certStatus.mail
                                })
                            }

                            // ユーザーが「パスワードを再変更」「メールアドレスを再変更」で明示的に
                            // フォームを開き直している間は、まだ何も送信していないのに現在のステータス
                            // （＝変更前のまま）で毎tick上書きして閉じ直してしまわないようにする。
                            if (!isReChangingPassword.value) {
                                showPWFormOverlay.value = !authStore.getters.shouldUpdatePassword
                            }
                            if (!isReChangingMail.value) {
                                showMailFormOverlay.value = !authStore.getters.shouldVerifyMail
                            }

                            if (!authStore.getters.shouldUpdateAuths
                                && !isReChangingPassword.value
                                && !isReChangingMail.value) {
                                // 更新処理が成功してその必要がなくなった。
                                // （ユーザーが再変更フォームを開いている最中は、まだ何も送信していない
                                // 現在のステータスだけを理由にオーバーレイ全体を閉じてしまわないようにする）
                                authUpdateOverlayStateStore.commit("hideOverlay")
                                toastStore.commit("post", {
                                    message: "認証情報の更新に成功しました。"
                                })
                            }

                        } else {
                            // 自発更新モードの時は問合せはメールに関するものだけを意識している。
                            if (certStatus.mail !== authStore.state.auth.cert.mail) {
                                authStore.commit("updateEmailValidity", {
                                    mail: certStatus.mail
                                })
                            }
                            if (!isReChangingMail.value) {
                                showMailFormOverlay.value = !authStore.getters.shouldVerifyMail
                            }
                        }

                    }  else {
                        authStore.commit("clearAuth")
                        authStore.commit("clearToken")
                        await router.push({
                            path: subPaths.LOGGED_OUT,
                            query: {
                                logout_message: logoutMessages.NORMAL_LOGOUT.key
                            }
                        })
                    }
                } else {
                    // shouldUpdateAuths が「このtickの開始時点で既に」falseだった場合（直前のtickで
                    // 変化を検知してhideOverlay済みのケース以外）にもここへ来る。SHOULD_UPDATE
                    // （強制的な初回設定フロー）ではポーリングを止めるだけでなくオーバーレイ自体も
                    // 閉じないと、show===trueのまま何も更新されなくなり永久に開いたまま残ってしまう。
                    // 一方 NORMAL_UPDATE（フッターからの任意の再変更）はshouldUpdateAuthsが常にfalseな
                    // アカウント向けの機能なので、ここで閉じてしまうと開いた直後に自動で閉じてしまう
                    // （ユーザーが「閉じる」を押すまで開いたままにするのが正しい挙動）。
                    if (authUpdateOverlayStateStore.state.mode === authUpdateOverlayMode.SHOULD_UPDATE
                        && authUpdateOverlayStateStore.state.show) {
                        authUpdateOverlayStateStore.commit("hideOverlay")
                    }
                    stopCertCheck()
                }
            }, 1000)
        }

        const stopCertCheck = () => {
            window.clearInterval(certCheckTimerId)
        }

        const sendMailButtonText = (): string => {
            return mailSentOnce.value ? "確認メールを再送信" : "確認メールを送信"
        }

        authUpdateOverlayStateStore.watch(state => state.show, show => {
            if (show) {
                resetInputs()

                // 要更新モードの時は目隠しのない状態で表示されるし、自発更新モードの時は目隠しをして「する必要がない経度するのだ」という認識を与える。
                showPWFormOverlay.value = !authStore.getters.shouldUpdatePassword
                showMailFormOverlay.value = !authStore.getters.shouldVerifyMail

                console.log(showPWFormOverlay.value, showMailFormOverlay.value)

                startCheckingCertStatus()
            } else {
                stopCertCheck()
            }
        })

        const onClickReChangePW = () => {
            showPWFormOverlay.value = false
            isReChangingPassword.value = true
        }

        const onClickReVerifyMail = () => {
            showMailFormOverlay.value = false
            isReChangingMail.value = true
        }

        return {
            authUpdateOverlayStateStore,
            FontSizes,
            authStore,
            password1,
            password2,
            pwWarningMessage,
            canClickUpdatePW,
            email,
            cancelText,
            canClickSendMail,
            onClickCancelUpdate,
            onClickUpdatePassword,
            onClickSendVerificationMail,
            sendMailButtonText,
            showPWFormOverlay,
            showMailFormOverlay,
            onClickReChangePW,
            onClickReVerifyMail,
        }
    }
})
</script>

<style scoped lang="scss">

.fade-enter-active,
.fade-leave-active {
    /* 表示されている際のCSSはこのブロックに記述 */
    transition: opacity .5s;
    pointer-events: auto;
}

.fade-enter-from,
.fade-leave-to {
    /* 非表示の際のCSSはこのブロックに記述 */
    opacity: 0;
    pointer-events: none;
}

.auth-update-overlay {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(5px);
    z-index: 2000;
    overflow: auto;

    .container-sm {
        .dummy-row {
            height: 20px;
        }

        .title-row {
            .title-col {
                height: 50px;
            }

        }

        .password-input-form {

            .warning-row {
                min-height: 30px;
                pointer-events: none;
            }

            .pw-warning-message {
                // バックエンドのdescriptionに\nが含まれる場合、改行として表示する。
                white-space: pre-line;
            }

            .password-input-form-overlay {

                .re-update-pw-button {
                }
            }
        }

        .mail-input-form {

            .mail-input-form-overlay {
                .re-update-mail-button {

                }
            }
        }
        .mail-explain {
            color: firebrick;
        }
        .input-form {

            border-radius: 10px;
            border: 3px mediumseagreen dashed;
            margin: 5px;

            &-should-update {
                border-color: red;
            }

            .input-form-overlay {
                position: absolute;
                width: 100%;
                height: 100%;
                top: 0;
                left: 0;
                border-radius: 10px;
                background-color: rgba(255, 255, 255, 0.8);
                backdrop-filter: blur(5px);

                .updated-text {
                    position: absolute;
                    left: 0;
                    right: 0;
                    top: 0;
                    bottom: 0;
                    margin: auto;
                    width: 200px;
                    height: 50px;
                    color: mediumseagreen;
                }


                .re-update-button {
                    position: absolute;
                    left: 0;
                    right: 0;
                    margin: 0 auto;
                    bottom: 10px;
                    width: 80%;
                }
            }
        }
    }

    .text-updated {
        color: mediumseagreen;
    }

    .text-should-update {
        color: red;
    }
}
</style>